<template>
  <div class="container pap-form">
    <div v-if="showDeleteAccountResult" class="mb-3">
      <AlertCard
        :type="resultType"
        :header="resultHeader"
        :message="resultMessage"
      />
    </div>

    <FormReauth
      v-if="showReauthForm"
      formTitle="Delete Account: Verify Credentials"
      actionStatement="delete your account"
      @reauth-validated="onValidReauth($event)"
    >
      The <strong>next</strong> page will have details about deleting your
      account along with a <span class="italic">Delete My Account</span> button.
    </FormReauth>

    <Form
      v-if="showDeleteAccountForm"
      key="deleteAccount"
      :data="deleteAccountForm"
      @form-validated="confirmDelete($event)"
    >
      <template v-slot:topText>
        <div class="pap-form__text">
          <p class="font-weight-bold">Oh no!</p>
          <p>We're sorry you want to delete your account!</p>
          <p>
            If there's anything we can do to possibly change your mind, please
            let us know <router-link to="/contact">here</router-link>.
          </p>
          <p class="font-weight-bold">
            How Deleting Your Account Affects Your Data
          </p>
          <p>
            Per the agreed-upon
            <a href="https://pixapup.com/terms-of-service" target="_blank"
              >Terms of Service</a
            >:
          </p>
          <ul>
            <li>
              Deleting your account results in the removal from the secure
              Google Cloud storage platform and databases we use of any of your
              identifying attributes, such as your email address. We will not
              contact you again as a result.
            </li>
            <li>
              Deleting your account results in the removal from the secure
              Google Cloud storage platform and database of any and all images
              and associated text you uploaded using the Pixapup app.
            </li>
            <li>
              If any of your uploaded images were published in one or more of
              our custom books, calendars, etc., those published works remain
              intact on the respective selling platforms/websites on which they
              currently exist.
            </li>
            <li>
              For any of your images that <strong>were</strong> published in one
              or more of our custom books, calendars, etc., the record created
              and stored when you uploaded each of those images
              <strong>does</strong> remain in the secure Google Cloud storage
              platform and databases we use. Per the agreed-upon Terms of
              Service, the purpose of keeping these particular upload records
              (of images that were published by us) is to help establish, if
              necessary, legal claim to our right to the use of any of your
              images and text under the terms of the Terms of Service agreement.
            </li>
          </ul>
          <p>
            If you wish to proceed with deleting your account, click the button
            below.
          </p>
          <p class="font-weight-bold">
            This is permanent and cannot be undone. Should you decide to sign up
            again, you may do so, even with the same email address as this
            account you are about to delete, but that new account will be
            considered and treated as a distinct, separate account with no ties
            to this account.
          </p>
          <p>We're sorry to see you go!</p>
        </div>
      </template>
    </Form>
  </div>
</template>

<script>
function detachDocListeners(docListeners) {
  return new Promise((res, rej) => {
    Object.values(docListeners).forEach((f) => f());
    res(true);
  });
}

import FormReauth from '@/components/form/FormReauth';
import Form from '@/components/form/Form';
import AlertCard from '@/components/notifications/AlertCard';
import { mapGetters } from 'vuex';

export default {
  name: 'DeleteAccount',
  components: {
    FormReauth,
    Form,
    AlertCard,
  },
  data: function () {
    return {
      showDeleteAccountResult: false,
      showReauthForm: true,
      resultType: '',
      resultSuccessMsg: '',
      resultErrorMsg: '',
      deleteAccountForm: this.deleteAccountFormData(),
    };
  },
  computed: {
    ...mapGetters('auth', ['authUser']),
    showDeleteAccountForm: function () {
      return !this.showDeleteAccountResult && !this.showReauthForm;
    },

    resultHeader: function () {
      if (this.resultType === 'success') {
        return 'Success!';
      }
      if (this.resultType === 'error') {
        return 'There was a problem...';
      }
      return '';
    },
    resultMessage: function () {
      return this.resultErrorMsg ? this.resultErrorMsg : this.resultSuccessMsg;
    },
  },
  methods: {
    deleteAccountFormData: function () {
      var formData = {
        formTitle: 'Delete Your Account',
        headerIconClass: 'fas fa-user-times mr-2',
        submitButtonText: 'Delete My Account...',
      };
      return formData;
    },

    onValidReauth: function () {
      this.showReauthForm = false;
    },

    confirmDelete: async function (formData) {
      if (formData) {
        try {
          console.log('confirm delete', formData);
          // Detach all FB document listeners
          var docListeners = await this.$store.getters.docListeners;
          var d = await detachDocListeners(docListeners);
          this.$store.dispatch('uploadTracker/clearImageCounts');
          this.$store.dispatch('uploadTracker/clearImageNamesAll');
          this.$store.dispatch('clearUserProfile');
          await this.$fapi.user.delete();
          // this.$router.push({ path: 'login', query: { redirect: 'logout' } });
          this.resultType = 'success';
          this.resultSuccessMsg = `Your account was successfully deleted.`;
          // throw Error('requires recent authentication so chop chop');
        } catch (err) {
          // If user sat on this page for too long, the attempt to set their email would fail due to needing to reauthenticate, i.e.
          // error would be thrown: 'This operation is sensitive and requires recent authentication. Log in again before retrying this request.'
          // Check if that's the error message. If so, show the reauth form to start over.
          if (/recent authentication/.test(err.message)) {
            this.showReauthForm = true;
            return;
          }
        }
        this.showDeleteAccountResult = true;
      }
    },
  },
};
</script>

<style>
</style>